import { useEffect, useRef, useState } from "react";
import { PhoneCall, Save, PlugZap, Volume2, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TimeField } from "@/components/DateTimeField";
import { ConfirmCallLog } from "@/components/ConfirmCallLog";

const VOICES = ["nova", "coral", "shimmer", "alloy", "sage", "onyx", "echo", "fable", "ash"];
const F = ({ label, children }) => <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;

export default function ConfirmCallSettings() {
  const [form, setForm] = useState(null);
  const [info, setInfo] = useState({});
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState("");
  const [logKey, setLogKey] = useState(0);
  const audioRef = useRef(null);

  const apply = (d) => { const { has_api_key, configured, default_message, ...rest } = d; setForm({ ...rest, api_key: "" }); setInfo({ has_api_key, configured, default_message }); };
  useEffect(() => { api.get("/settings/confirm-calls").then((r) => apply(r.data)).catch(() => {}); }, []);
  if (!form) return null;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); } };

  const save = () => run("save", async () => { const { data } = await api.put("/settings/confirm-calls", form); apply(data); toast.success("Impostazioni chiamate salvate"); });
  const check = () => run("check", async () => { await api.post("/settings/confirm-calls/check"); toast.success("Connessione al 3CX riuscita"); });
  const listen = () => run("listen", async () => {
    const r = await api.post("/settings/confirm-calls/preview-audio", null, { responseType: "blob" });
    audioRef.current.src = URL.createObjectURL(r.data); audioRef.current.play();
  });
  const testCall = () => run("test", async () => { await api.post("/settings/confirm-calls/test-call", { phone }); toast.info("Chiamata di prova avviata: l'esito compare nell'elenco"); setLogKey((k) => k + 1); });

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="confirm-calls-settings">
      <div className="mb-1 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2"><PhoneCall size={18} className="text-primary" /><h3 className="font-bold text-navy">Chiamate di conferma appuntamento (3CX)</h3></div>
        <label className="flex items-center gap-2 text-sm font-semibold">Attive<Switch checked={form.enabled} onCheckedChange={set("enabled")} data-testid="cc-enabled" /></label>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">Il giorno prima dell'appuntamento cliente, all'ora scelta, Flux chiama ogni cliente con voce sintetica: tasto 1 conferma, tasto 2 chiede di spostare. Se non risponde riprova una volta. L'esito finisce nelle note del ticket; se non è una conferma avvisa il Responsabile Delivery.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Indirizzo del centralino 3CX"><Input value={form.fqdn} onChange={set("fqdn")} placeholder="powercom.3cx.it" data-testid="cc-fqdn" /></F>
        <F label="Route Point (interno)"><Input value={form.dn} onChange={set("dn")} placeholder="900" data-testid="cc-dn" /></F>
        <F label="Client ID"><Input value={form.client_id} onChange={set("client_id")} data-testid="cc-client-id" /></F>
        <F label="Chiave API"><Input type="password" value={form.api_key} onChange={set("api_key")} placeholder={info.has_api_key ? "•••••••• (salvata)" : ""} autoComplete="new-password" data-testid="cc-api-key" /></F>
        <F label="Ora delle chiamate (giorno prima)"><div className="w-40"><TimeField value={form.time} onChange={set("time")} testid="cc-time" /></div></F>
        <F label="Secondo tentativo dopo (minuti)"><Input type="number" min={10} max={240} value={form.retry_minutes} onChange={(e) => set("retry_minutes")(parseInt(e.target.value) || 60)} className="w-32" data-testid="cc-retry" /></F>
        <F label="Voce">
          <Select value={form.voice} onValueChange={set("voice")}>
            <SelectTrigger className="w-48" data-testid="cc-voice"><SelectValue /></SelectTrigger>
            <SelectContent>{VOICES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </F>
      </div>
      <div className="mt-4 space-y-1.5">
        <Label>Messaggio</Label>
        <Textarea rows={4} value={form.message} onChange={set("message")} data-testid="cc-message" />
        <p className="text-xs text-muted-foreground">Segnaposto: {"{cliente} {data} {ora} {indirizzo} {committente} {codice}"}. <button type="button" className="font-semibold text-primary" onClick={() => set("message")(info.default_message)}>Ripristina testo standard</button></p>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button onClick={save} disabled={!!busy} data-testid="cc-save"><Save size={16} />Salva</Button>
        <Button variant="outline" onClick={check} disabled={!!busy} data-testid="cc-check"><PlugZap size={16} />Verifica connessione</Button>
        <Button variant="outline" onClick={listen} disabled={!!busy} data-testid="cc-listen"><Volume2 size={16} />{busy === "listen" ? "Genero…" : "Ascolta messaggio"}</Button>
        <audio ref={audioRef} className="hidden" />
      </div>
      <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-border pt-4">
        <F label="Chiamata di prova al numero"><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+39 333 1234567" className="w-60" data-testid="cc-test-phone" /></F>
        <Button variant="outline" onClick={testCall} disabled={!!busy || !phone} data-testid="cc-test-call"><Phone size={16} />Chiama ora</Button>
      </div>
      <ConfirmCallLog refreshKey={logKey} />
    </div>
  );
}
